import React, { useEffect, useCallback, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import CityMap from "../../components/Map/CityMap";
import { SidebarContext } from "../../context/SidebarContext.js";
import { config } from "../../config.js";
import { LuArrowLeft } from "react-icons/lu";

function City() {
  const { isSidebar } = useContext(SidebarContext);
  const { uid } = useParams();
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  /* -------------------- STATE -------------------- */

  // Page-level loading (initial load only)
  const [pageLoading, setPageLoading] = useState(true);

  // Boundary save/update loading (map-only)
  const [boundaryLoading, setBoundaryLoading] = useState(false);

  const [city, setCity] = useState(null);

  /* -------------------- FETCH CITY -------------------- */
  // Supports both full load and silent refresh
  const fetchCity = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setPageLoading(true);

      try {
        const response = await fetch(
          `${config.backEndBaseUrl}city/get/${uid}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();
        setCity(result.data);
      } catch (error) {
        console.error("Fetching city failed:", error);
        setCity(null);
      } finally {
        if (!silent) setPageLoading(false);
      }
    },
    [uid],
  );

  /* -------------------- INITIAL LOAD -------------------- */

  useEffect(() => {
    fetchCity(); // full page loading
  }, [fetchCity]);

  /* -------------------- SAVE / UPDATE BOUNDARY -------------------- */

  const setBoundary = useCallback(
    async (boundaryData) => {
      if (!uid || !boundaryData?.length) return;

      const hasExistingBoundary =
        Array.isArray(city?.boundaries?.boundary) &&
        city.boundaries.boundary.length > 0;

      const method = hasExistingBoundary ? "PUT" : "POST";
      const url = `${config.backEndBaseUrl}city/${uid}/boundary`;

      try {
        setBoundaryLoading(true);

        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ boundary: boundaryData }),
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Boundary save failed");
        }

        await response.json();

        //  refresh city silently (NO page skeleton)
        fetchCity({ silent: true });
      } catch (error) {
        console.error("Boundary save failed:", error);
      } finally {
        setBoundaryLoading(false);
      }
    },
    [uid, city, fetchCity],
  );

  /* -------------------- UI -------------------- */

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />

            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : "0px" }}
              >
                <div className="main-content-inner">
                  <div className="main-content-wrap">
                    <div className="tf-section">
                      {/* -------- FULL PAGE LOADING -------- */}
                      {pageLoading && (
                        <div className="wg-box">
                          <div
                            style={{
                              height: 28,
                              width: 220,
                              borderRadius: 4,
                              marginBottom: 20,
                              background:
                                "linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%)",
                              backgroundSize: "400% 100%",
                              animation: "shimmer 1.4s ease infinite",
                            }}
                          />
                          <div
                            style={{
                              width: "100%",
                              height: 450,
                              borderRadius: 8,
                              background:
                                "linear-gradient(90deg,#eee 25%,#f5f5f5 37%,#eee 63%)",
                              backgroundSize: "400% 100%",
                              animation: "shimmer 1.4s ease infinite",
                            }}
                          />
                        </div>
                      )}

                      {/* -------- CONTENT -------- */}
                      {!pageLoading && city && (
                        <div className="wg-box relative">
                          <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                              <button
                                className="back-btn flex items-center gap-2"
                                onClick={() => navigate(-1)}
                              >
                                <LuArrowLeft size={16} />
                                {/* <span>Back</span> */}
                              </button>

                              <h5 className="fare-heading">
                                City – {city.cityName}
                              </h5>
                            </div>
                          </div>

                          {/* -------- MAP LOADING OVERLAY -------- */}
                          {boundaryLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white shadow">
                                <svg
                                  className="h-5 w-5 animate-spin text-green-800"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                  />
                                </svg>
                                <span className="text-sm font-medium text-gray-700">
                                  Saving boundary…
                                </span>
                              </div>
                            </div>
                          )}

                          <CityMap
                            boundaries={city?.boundaries?.boundary || []}
                            onSave={setBoundary}
                            disabled={boundaryLoading}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default City;
